// CRM Pipeline (Kanban) screen. Uses HTML5 native drag-and-drop — no library —
// because we don't need touch reordering or rich animations yet. If/when we do,
// swap the dataTransfer handlers for @hello-pangea/dnd without changing the API.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Dropdown, Select as AntSelect, Tooltip } from "antd";
import {
  Plus, Loader2, AlertCircle, GitBranch, MoreHorizontal, Trash2, Pencil,
  UserPlus, Mail, Globe, Bell, CheckCircle2, AlertTriangle, CalendarClock,
  XCircle, Download, ChevronLeft, ChevronRight, ChevronDown,
} from "lucide-react";
import { Button, Badge } from "../atoms";
import Modal from "../overlays/Modal";
import ConfirmDeleteModal from "../overlays/ConfirmDeleteModal";
import LeadDrawer from "../organisms/LeadDrawer";
import PageHeader from "../organisms/PageHeader";
import CallButton from "../organisms/calls/CallButton";
import BulkAddLeadsModal from "../molecules/BulkAddLeadsModal";
import { useTeamMembers, formatMember, useMyCrmRole } from "../hooks/useTeamMembers";
import { useOrgCurrency } from "../hooks/useOrgCurrency";
import { formatCurrency as libFormatCurrency } from "../lib/currency";
import { websiteHref } from "../lib/links";
import {
  usePipelines,
  useCreatePipeline,
  useAddStage,
  useUpdateStage,
  useDeleteStage,
  useMoveLead,
} from "../hooks/usePipelines";
import { useLeads, fetchLeadsPage } from "../hooks/useLeads";
import EmailFollowUpPill from "../molecules/EmailFollowUpPill";

const DRAG_TYPE = "application/x-crm-lead";

// ─── Lead status helpers ──────────────────────────────────────────────────────
const STATUS_STYLE = {
  hot:  { label: "Hot",  bg: "rgba(239,68,68,0.12)",  fg: "rgb(220,38,38)"  },
  warm: { label: "Warm", bg: "rgba(245,158,11,0.13)", fg: "rgb(180,83,9)"   },
  cold: { label: "Cold", bg: "rgba(59,130,246,0.12)", fg: "rgb(37,99,235)"  },
};

function LeadStatusPill({ status }) {
  const s = STATUS_STYLE[status];
  if (!s) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "1px 7px", borderRadius: 4,
      fontSize: 11, fontWeight: 500, lineHeight: "18px",
      background: s.bg, color: s.fg,
      flexShrink: 0,
    }}>
      {s.label}
    </span>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

// ─── Idle-time helpers ────────────────────────────────────────────────────────
const MS_PER_DAY = 86_400_000;

function idleDays(addedAt) {
  if (!addedAt) return null;
  const ms = Date.now() - new Date(addedAt).getTime();
  if (ms < 0) return 0;
  return Math.floor(ms / MS_PER_DAY);
}

function idleTier(days) {
  if (days === null) return null;
  if (days < 3) return "active";
  if (days < 7) return "cooling";
  return "stale";
}

const IDLE_STYLES = {
  active:  { bg: "rgba(16,185,129,0.12)", fg: "rgb(16,185,129)", label: (d) => `${d}d idle`, accent: "rgb(16,185,129)"  },
  cooling: { bg: "rgba(245,158,11,0.14)", fg: "rgb(180,83,9)",   label: (d) => `${d}d idle`, accent: "rgb(245,158,11)" },
  stale:   { bg: "rgba(239,68,68,0.13)",  fg: "rgb(220,38,38)",  label: (d) => `${d}d idle`, accent: "rgb(239,68,68)"  },
};

function IdleBadge({ addedAt }) {
  const days = idleDays(addedAt);
  if (days === null || days === 0) return null;
  const tier = idleTier(days);
  const s = IDLE_STYLES[tier];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "1px 6px", borderRadius: 4,
      fontSize: 11, fontWeight: 500, lineHeight: "18px",
      background: s.bg, color: s.fg,
      flexShrink: 0,
    }}>
      {s.label(days)}
    </span>
  );
}

const IDLE_FILTERS = [
  { key: "all", label: "All",         min: null, max: null },
  { key: "3",   label: "3–6 days",   min: 3,    max: 6    },
  { key: "7",   label: "7–13 days",  min: 7,    max: 13   },
  { key: "14",  label: "14+ days",   min: 14,   max: null },
];

function idleFilterMatch(entry, filterKey) {
  if (filterKey === "all") return true;
  const f = IDLE_FILTERS.find((x) => x.key === filterKey);
  if (!f) return true;
  const days = idleDays(entry.addedAt);
  if (days === null) return false;
  if (f.min !== null && days < f.min) return false;
  if (f.max !== null && days > f.max) return false;
  return true;
}
// ─────────────────────────────────────────────────────────────────────────────

function formatCurrency(n, ccy) {
  return libFormatCurrency(n || 0, ccy || "USD") || "0";
}

function stageValue(stage) {
  return (stage.leads || []).reduce((acc, e) => acc + (e.lead?.value || 0), 0);
}

function stageProbability(stage, stages) {
  // Linear ramp: first stage 10%, last stage 90%. If `probability` is set
  // explicitly on the stage doc, prefer that. Single-stage pipelines get 50%.
  if (typeof stage.probability === "number") return Math.max(0, Math.min(100, Math.round(stage.probability)));
  const list = stages || [];
  const idx = list.findIndex((s) => String(s._id) === String(stage._id));
  if (idx < 0 || list.length <= 1) return 50;
  const pct = 10 + (80 * idx) / (list.length - 1);
  return Math.round(pct);
}

function initialsOf(s) {
  if (!s) return "?";
  const parts = String(s).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]).join("").toUpperCase() || "?";
}

function formatRelative(d) {
  if (!d) return "";
  const t = new Date(d).getTime();
  const diff = Date.now() - t;
  if (diff < 0) {
    const fwd = -diff;
    if (fwd < 86400_000) return "today";
    const days = Math.floor(fwd / 86400_000);
    if (days < 7) return `in ${days}d`;
    if (days < 30) return `in ${Math.floor(days / 7)}w`;
    return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  if (diff < 3600_000) return "just now";
  if (diff < 86400_000) return "today";
  if (diff < 2 * 86400_000) return "yesterday";
  const days = Math.floor(diff / 86400_000);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatShortDate(d) {
  if (!d) return "";
  const date = new Date(d);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, sameYear
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" });
}

// Tones reuse existing CSS vars — no new theme tokens introduced.
function FollowUpPill({ followUp }) {
  if (!followUp || !followUp.scheduledAt) return null;
  const status = followUp.status || "pending";
  const due = new Date(followUp.scheduledAt).getTime();
  const overdue = status === "pending" && due < Date.now();
  let Icon = Bell;
  let color = "var(--crm-warning, #B45309)";
  let label = `Follow-up ${formatRelative(followUp.scheduledAt)}`;
  if (status === "completed") { Icon = CheckCircle2; color = "var(--crm-success)"; label = "Followed up"; }
  else if (status === "missed" || overdue) { Icon = AlertTriangle; color = "var(--crm-danger)"; label = `Overdue ${formatRelative(followUp.scheduledAt)}`; }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color }}>
      <Icon size={11} /> {label}
    </span>
  );
}

function OwnerAvatar({ ownerId, ownerEmail, byId }) {
  const member = ownerId ? byId.get(String(ownerId)) : null;
  const display = member ? formatMember(member) : (ownerEmail || "");
  if (!display) return null;
  return (
    <Tooltip title={`Owner: ${display}`} mouseEnterDelay={0.3}>
      <span
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          flexShrink: 0, minWidth: 0, maxWidth: 130,
        }}
      >
        {member?.avatarUrl ? (
          <img
            src={member.avatarUrl}
            alt=""
            style={{
              width: 22, height: 22, borderRadius: 9999,
              objectFit: "cover", flexShrink: 0,
              border: "1px solid var(--color-border-subtle)",
            }}
          />
        ) : (
          <span style={{
            width: 22, height: 22, borderRadius: 9999, flexShrink: 0,
            background: "var(--color-bg-muted)", color: "var(--color-text-secondary)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, fontWeight: 600, border: "1px solid var(--color-border-subtle)",
          }}>
            {initialsOf(display)}
          </span>
        )}
        <span style={{
          fontSize: 11, fontWeight: 600,
          color: "var(--color-text-secondary)",
          minWidth: 0, flex: 1,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {display}
        </span>
      </span>
    </Tooltip>
  );
}

function CardActionIcon({ href, onClick, title, children, danger }) {
  const common = {
    width: 26, height: 26, borderRadius: 6,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    color: "var(--color-text-secondary)", background: "transparent",
    border: "1px solid var(--color-border-subtle)", cursor: "pointer",
    textDecoration: "none",
  };
  if (href) {
    return (
      <a href={href} title={title} onClick={(e) => e.stopPropagation()} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" style={common}>
        {children}
      </a>
    );
  }
  return <button type="button" title={title} onClick={(e) => { e.stopPropagation(); onClick?.(); }} style={common}>{children}</button>;
}

function LeadCard({ entry, onDragStart, onClick, byId, orgCurrency, idleFilter }) {
  const lead = entry.lead;
  const { isOwner } = useMyCrmRole();
  if (!lead) return null;
  const labels = Array.isArray(lead.labels) ? lead.labels : [];
  const shownLabels = labels.slice(0, 2);
  const overflow = labels.length - shownLabels.length;
  const siteHref = websiteHref(lead.website);

  const filterActive = idleFilter && idleFilter !== "all";
  const days = idleDays(entry.addedAt);
  const tier = filterActive && days !== null && days > 0 ? idleTier(days) : null;
  const accent = tier ? IDLE_STYLES[tier].accent : null;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, entry)}
      onClick={() => onClick(lead)}
      style={{
        background: "var(--color-bg-surface)",
        border: "1px solid var(--color-border-subtle)",
        borderLeft: accent ? `3px solid ${accent}` : "1px solid var(--color-border-subtle)",
        borderRadius: 8,
        padding: accent ? "12px 12px 12px 10px" : 12,
        cursor: "grab",
        userSelect: "none",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        display: "flex", flexDirection: "column", gap: 8,
        flexShrink: 0,
        transition: "box-shadow 0.15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = accent ? `0 2px 8px rgba(0,0,0,0.10), -1px 0 0 ${accent}` : "0 2px 8px rgba(0,0,0,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; }}
    >
      {/* Row 1 — avatar · title+brand · call */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, flexShrink: 0,
          background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 500, fontSize: 12,
        }}>
          {initialsOf(lead.brandName || lead.title)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            {/* Name owns the row: it never shrinks below readability, and any
                long label/+N is pushed to a chip that truncates instead. */}
            <Tooltip title={lead.title} mouseEnterDelay={0.3}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0, maxWidth: "100%" }}>
                {lead.title}
              </span>
            </Tooltip>
            {shownLabels.map((t) => (
              <Tooltip key={t} title={t} mouseEnterDelay={0.3}>
                <span style={{ display: "inline-flex", minWidth: 0, flexShrink: 1, overflow: "hidden" }}>
                  <Badge tone="neutral">
                    <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }}>{t}</span>
                  </Badge>
                </span>
              </Tooltip>
            ))}
            {overflow > 0 && (
              <Tooltip title={labels.slice(2).join(", ")} mouseEnterDelay={0.3}>
                <span style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)", flexShrink: 0 }}>
                  +{overflow}
                </span>
              </Tooltip>
            )}
          </div>
          {(lead.brandName || lead.source) && (
            <Tooltip
              title={[lead.brandName, lead.source].filter(Boolean).join(" · ")}
              mouseEnterDelay={0.3}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2, fontSize: 11, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {lead.brandName && <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{lead.brandName}</span>}
                {lead.brandName && lead.source && <span>·</span>}
                {lead.source && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                    <Globe size={10} /> {lead.source}
                  </span>
                )}
              </div>
            </Tooltip>
          )}
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <CallButton compact leadId={lead._id} leadPhone={lead.phone} leadTitle={lead.title} />
        </div>
      </div>

      {/* Row 2 — next activity. Strict one-line clip + tooltip on hover so
          the column width stays predictable regardless of the user's text. */}
      {lead.nextActivity && (
        <Tooltip title={lead.nextActivity} mouseEnterDelay={0.3}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "4px 8px", borderRadius: 6,
            background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
            fontSize: 11, fontWeight: 600,
            maxWidth: "100%", minWidth: 0,
          }}>
            <CalendarClock size={11} style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>
              {lead.nextActivity}
            </span>
          </div>
        </Tooltip>
      )}

      {/* Row 3 — follow-up pill */}
      <FollowUpPill followUp={lead.followUp} />
      <EmailFollowUpPill summary={lead.emailFollowUpSummary} />

      {/* Row 4 — value · owner · expected close */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)", fontVariantNumeric: "tabular-nums" }}>
          {formatCurrency(lead.value, lead.currency || orgCurrency)}
        </div>
        <div style={{ flex: 1 }} />
        <LeadStatusPill status={lead.status} />
        <IdleBadge addedAt={entry.addedAt} />
        {lead.expectedCloseDate && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, color: "var(--crm-warning, #B45309)", fontWeight: 600 }}>
            <CalendarClock size={10} /> {formatShortDate(lead.expectedCloseDate)}
          </span>
        )}
        {isOwner && <OwnerAvatar ownerId={lead.ownerId} ownerEmail={lead.ownerEmail} byId={byId} />}
      </div>

      {/* Row 5 — time-ago + bottom action icons */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, borderTop: "1px solid var(--color-border-subtle)", paddingTop: 8, marginTop: 2 }}>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)", flex: 1 }}>
          {formatRelative(lead.updatedAt || lead.createdAt)}
        </span>
        {lead.email && (
          <CardActionIcon href={`mailto:${lead.email}`} title={`Email ${lead.email}`}>
            <Mail size={12} />
          </CardActionIcon>
        )}
        {siteHref && (
          <CardActionIcon href={siteHref} title={lead.website}>
            <Globe size={12} />
          </CardActionIcon>
        )}
      </div>
    </div>
  );
}

function StageHeader({ stage, pipelineId, onRenamed, onDeleted, probability, orgCurrency }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [name, setName] = useState(stage.name);
  const updateStage = useUpdateStage();
  const deleteStage = useDeleteStage();

  const performDelete = () => {
    deleteStage.mutate({ pipelineId, stageId: stage._id }, {
      onSuccess: () => { setConfirmOpen(false); onDeleted?.(); toast.success("Stage deleted"); },
      onError: (e) => toast.error(e?.response?.data?.error || "Delete failed"),
    });
  };

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === stage.name) { setRenaming(false); setName(stage.name); return; }
    updateStage.mutate({ pipelineId, stageId: stage._id, name: trimmed }, {
      onSuccess: () => { setRenaming(false); onRenamed?.(); toast.success("Stage renamed"); },
      onError: (e) => toast.error(e?.response?.data?.error || "Rename failed"),
    });
  };

  return (
    <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--color-border-subtle)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        {renaming ? (
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={submit}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") { setRenaming(false); setName(stage.name); } }}
            style={{ flex: 1, fontSize: 13, fontWeight: 600, background: "var(--color-bg-base)", border: "1px solid var(--crm-primary)", borderRadius: 6, padding: "4px 8px", color: "var(--color-text-primary)", outline: "none" }}
          />
        ) : (
          <Tooltip title={stage.name} mouseEnterDelay={0.3}>
            <div style={{
              fontSize: 12, fontWeight: 600, color: "var(--color-text-primary)",
              minWidth: 0, flex: 1,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {stage.name}
              <span style={{ marginLeft: 8, color: "var(--color-text-secondary)", fontWeight: 500 }}>
                · {(stage.leads || []).length}
              </span>
            </div>
          </Tooltip>
        )}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            style={{ background: "transparent", border: "none", color: "var(--color-text-secondary)", cursor: "pointer", padding: 4, borderRadius: 6, display: "flex" }}
            aria-label="Stage menu"
          >
            <MoreHorizontal size={16} />
          </button>
          {menuOpen && (
            <>
              <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 10 }} />
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 4, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", zIndex: 11, minWidth: 140 }}>
                <button
                  onClick={() => { setMenuOpen(false); setRenaming(true); }}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", background: "transparent", border: "none", borderRadius: 6, fontSize: 13, color: "var(--color-text-primary)", cursor: "pointer", textAlign: "left" }}
                >
                  <Pencil size={13} /> Rename
                </button>
                <button
                  onClick={() => { setMenuOpen(false); setConfirmOpen(true); }}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", background: "transparent", border: "none", borderRadius: 6, fontSize: 13, color: "var(--crm-danger)", cursor: "pointer", textAlign: "left" }}
                >
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <div style={{ marginTop: 6, display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, color: "var(--color-text-secondary)", fontVariantNumeric: "tabular-nums" }}>
        <span>{formatCurrency(stageValue(stage), orgCurrency)}</span>
        {typeof probability === "number" && (
          <span title="Win probability for this stage" style={{
            padding: "1px 6px", borderRadius: 4,
            background: "var(--color-bg-surface)",
            border: "1px solid var(--color-border-subtle)",
            color: "var(--color-text-secondary)", fontWeight: 600,
          }}>
            {probability}%
          </span>
        )}
      </div>
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={performDelete}
        title="Delete stage?"
        message={`"${stage.name}" will be removed from this pipeline. Leads currently in this stage will be removed from the pipeline (the leads themselves stay).`}
        confirmLabel="Delete stage"
        isPending={deleteStage.isPending}
      />
    </div>
  );
}

function StageColumn({ pipelineId, stage, onCardClick, onMoveTo, byId, probability, orgCurrency, idleFilter }) {
  const [dragOver, setDragOver] = useState(false);
  const visibleEntries = (stage.leads || []).filter((e) => idleFilterMatch(e, idleFilter || "all"));

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const raw = e.dataTransfer.getData(DRAG_TYPE);
        if (!raw) return;
        try {
          const payload = JSON.parse(raw);
          onMoveTo(payload, stage._id);
        } catch { /* ignore malformed drag payload */ }
      }}
      style={{
        // Hard-pinned column width. flex 0/0 + matching width+min/maxWidth so
        // no internal content (long stage name, big currency total in
        // StageHeader) can override the basis the way pure `flex` allows.
        // 320px gives lead names enough room before truncating (e.g.
        // "Shivam Srivastava", "Prakash Sharma") while keeping columns compact.
        flex: "0 0 320px",
        width: 320,
        minWidth: 320,
        maxWidth: 320,
        background: dragOver ? "var(--crm-primary-soft)" : "var(--color-bg-muted)",
        border: dragOver ? "1px dashed var(--crm-primary)" : "1px solid var(--color-border-subtle)",
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        // No `overflow: hidden` on the column itself — the inner lane handles
        // its own scrolling. Letting the column hide overflow was clipping
        // the bottom of taller cards.
        transition: "background 0.12s, border-color 0.12s",
      }}
    >
      <StageHeader stage={stage} pipelineId={pipelineId} probability={probability} orgCurrency={orgCurrency} />
      <div className="crm-stage-lane" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
        {(stage.leads || []).length === 0 && (
          <div style={{ padding: "16px 8px", textAlign: "center", fontSize: 12, color: "var(--color-text-secondary)" }}>
            Drop a lead here
          </div>
        )}
        {(stage.leads || []).length > 0 && visibleEntries.length === 0 && (
          <div style={{ padding: "16px 8px", textAlign: "center", fontSize: 12, color: "var(--color-text-secondary)" }}>
            No leads match idle filter
          </div>
        )}
        {visibleEntries.map((entry) => (
          <LeadCard
            key={String(entry.leadId)}
            entry={entry}
            byId={byId}
            orgCurrency={orgCurrency}
            onClick={onCardClick}
            idleFilter={idleFilter}
            onDragStart={(e, ent) => {
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({
                leadId: String(ent.leadId),
                fromStageId: String(stage._id),
              }));
            }}
          />
        ))}
      </div>
    </div>
  );
}

function AddStageColumn({ pipelineId }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const addStage = useAddStage();
  return (
    <div style={{ flex: "0 0 240px", width: 240, minWidth: 240, maxWidth: 240 }}>
      {open ? (
        <div style={{ background: "var(--color-bg-surface)", border: "1px solid var(--crm-primary)", borderRadius: 8, padding: 12 }}>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Stage name"
            onKeyDown={(e) => {
              if (e.key === "Escape") { setOpen(false); setName(""); }
              if (e.key === "Enter") {
                if (!name.trim()) return;
                addStage.mutate({ pipelineId, name: name.trim() }, {
                  onSuccess: () => { setOpen(false); setName(""); toast.success("Stage added"); },
                  onError: (err) => toast.error(err?.response?.data?.error || "Add failed"),
                });
              }
            }}
            style={{ width: "100%", fontSize: 14, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--color-border-subtle)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", outline: "none", marginBottom: 8 }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            <Button
              variant="primary"
              size="sm"
              disabled={!name.trim() || addStage.isPending}
              onClick={() => addStage.mutate({ pipelineId, name: name.trim() }, {
                onSuccess: () => { setOpen(false); setName(""); toast.success("Stage added"); },
                onError: (err) => toast.error(err?.response?.data?.error || "Add failed"),
              })}
            >
              {addStage.isPending ? "Adding…" : "Add"}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => { setOpen(false); setName(""); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          style={{ width: "100%", padding: "14px 12px", borderRadius: 8, border: "1px dashed var(--color-border-subtle)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 13, fontWeight: 500 }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--crm-primary)"; e.currentTarget.style.color = "var(--crm-primary)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--color-border-subtle)"; e.currentTarget.style.color = "var(--color-text-secondary)"; }}
        >
          <Plus size={14} /> Add stage
        </button>
      )}
    </div>
  );
}

function NewPipelineModal({ open, onClose }) {
  const [name, setName] = useState("");
  const createPipeline = useCreatePipeline();
  useEffect(() => { if (open) setName(""); }, [open]);
  const submit = () => {
    if (!name.trim()) return;
    createPipeline.mutate({ name: name.trim() }, {
      onSuccess: () => { onClose(); toast.success("Pipeline created"); },
      onError: (e) => toast.error(e?.response?.data?.error || "Create failed"),
    });
  };
  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 20 }}>
        <h2 style={{ fontSize: 17, fontWeight: 600, color: "var(--color-text-primary)", margin: 0 }}>New pipeline</h2>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 6 }}>Name</label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="e.g. Q2 Outbound"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--color-border-subtle)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", fontSize: 14, outline: "none" }}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--color-text-secondary)" }}>
            Starts with default stages: Qualified · Contact Made · Proposal Sent · Negotiation. Edit them after creation.
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!name.trim() || createPipeline.isPending}>
            {createPipeline.isPending ? "Creating…" : "Create"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const WON_LOST_PAGE_SIZE = 25;

function csvCell(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCsv(text, filename) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
function wonLostToCsv(rows, isWon, byId) {
  const cols = isWon
    ? ["title", "brandName", "email", "phone", "value", "currency", "source", "owner", "wonAt"]
    : ["title", "brandName", "email", "phone", "value", "currency", "source", "owner", "lostAt", "lostReason"];
  const header = cols.join(",");
  const body = rows.map((l) => cols.map((c) => {
    if (c === "owner") {
      const m = l.ownerId ? byId.get(String(l.ownerId)) : null;
      return csvCell(m ? formatMember(m) : "");
    }
    if (c === "wonAt" || c === "lostAt") return csvCell(l[c] ? new Date(l[c]).toISOString() : "");
    return csvCell(l[c]);
  }).join(",")).join("\n");
  return `${header}\n${body}\n`;
}

// Won / Lost leads table view — what we drop in when the user toggles the
// Potential/Won/Lost filter away from "active". Old CRM uses the same flow:
// inside the Pipeline screen, status=won|lost replaces the board with a flat
// list (no stages — these leads are out of the funnel). Each row shows the
// closing date and, for lost, the reason captured at mark-lost time.
function WonLostList({ status, byId, orgCurrency, onLeadClick, onItemsLoaded }) {
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const { isOwner, isAdmin } = useMyCrmRole();
  const isWon = status === "won";
  const sortField = isWon ? "wonAt" : "lostAt";

  const { data, isLoading, isError, error } = useLeads({
    status,
    limit: WON_LOST_PAGE_SIZE,
    skip: page * WON_LOST_PAGE_SIZE,
    sort: sortField,
    order: "desc",
  });
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / WON_LOST_PAGE_SIZE));

  useEffect(() => {
    onItemsLoaded?.(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Reset to page 0 when switching won/lost
  useEffect(() => { setPage(0); }, [status]);

  const tone = isWon ? "var(--crm-success)" : "var(--crm-danger)";
  const toneSoft = isWon ? "var(--crm-success-soft)" : "var(--crm-danger-soft)";
  const Icon = isWon ? CheckCircle2 : XCircle;

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const all = [];
      const EXPORT_PAGE = 200;
      for (let skip = 0; skip < total; skip += EXPORT_PAGE) {
        const res = await fetchLeadsPage({ status, sort: sortField, order: "desc", limit: Math.min(EXPORT_PAGE, total - skip), skip });
        all.push(...(res.items || []));
        if (!res.items?.length) break;
      }
      if (all.length === 0) { toast.error("Nothing to export"); return; }
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(wonLostToCsv(all, isWon, byId), `${isWon ? "won" : "lost"}-leads-${stamp}.csv`);
      toast.success(`Exported ${all.length} lead${all.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }, [status, sortField, total, isWon, byId]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
        padding: "10px 14px", borderRadius: 8,
        background: toneSoft, border: `1px solid color-mix(in srgb, ${tone} 25%, transparent)`,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: tone, color: "#fff",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
            {isWon ? "Won leads" : "Lost leads"}
          </div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
            {isLoading ? "Loading…" : `${total} ${total === 1 ? "lead" : "leads"} in this project`}
          </div>
        </div>
        {isAdmin && total > 0 && (
          <Button variant="secondary" size="sm" leftIcon={<Download size={13} />} onClick={exportCsv} disabled={exporting}>
            {exporting ? "Exporting…" : "Export"}
          </Button>
        )}
      </div>

      {isLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", padding: 40, color: "var(--color-text-secondary)" }}>
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      )}

      {isError && (
        <div style={{ background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 14, borderRadius: 8, fontSize: 13 }}>
          {error?.response?.data?.error || error?.message || "Failed to load"}
        </div>
      )}

      {!isLoading && !isError && total === 0 && (
        <div style={{ textAlign: "center", padding: "60px 24px", color: "var(--color-text-secondary)" }}>
          <Icon size={36} style={{ opacity: 0.3, marginBottom: 10 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)" }}>
            No {isWon ? "won" : "lost"} leads yet
          </div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            {isWon ? "Mark deals won from a lead drawer to see them here." : "Lost leads with reasons will appear here."}
          </div>
        </div>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div style={{
          flex: 1, minHeight: 0, overflowY: "auto",
          background: "var(--color-bg-surface)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: 8,
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--color-bg-muted)" }}>
                <th style={{ ...thStyle, position: "sticky", top: 0, zIndex: 1, background: "var(--color-bg-muted)" }}>Lead</th>
                {isOwner && <th style={{ ...thStyle, position: "sticky", top: 0, zIndex: 1, background: "var(--color-bg-muted)" }}>Owner</th>}
                <th style={{ ...thStyle, textAlign: "right", position: "sticky", top: 0, zIndex: 1, background: "var(--color-bg-muted)" }}>Value</th>
                <th style={{ ...thStyle, position: "sticky", top: 0, zIndex: 1, background: "var(--color-bg-muted)" }}>{isWon ? "Won on" : "Lost on"}</th>
                {!isWon && <th style={{ ...thStyle, position: "sticky", top: 0, zIndex: 1, background: "var(--color-bg-muted)" }}>Reason</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((lead) => {
                const owner = lead.ownerId ? byId.get(String(lead.ownerId)) : null;
                const closedAt = isWon ? lead.wonAt : lead.lostAt;
                return (
                  <tr
                    key={lead._id}
                    onClick={() => onLeadClick?.(lead._id)}
                    style={{ cursor: "pointer", borderTop: "1px solid var(--color-border-subtle)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-bg-muted)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, color: "var(--color-text-primary)" }}>{lead.title}</div>
                      {lead.brandName && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>{lead.brandName}</div>}
                    </td>
                    {isOwner && (
                      <td style={{ ...tdStyle, color: "var(--color-text-secondary)" }}>
                        {owner ? formatMember(owner) : "—"}
                      </td>
                    )}
                    <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                      {formatCurrency(lead.value, lead.currency || orgCurrency)}
                    </td>
                    <td style={tdStyle} title={closedAt ? new Date(closedAt).toLocaleString() : ""}>
                      {closedAt ? (
                        <>
                          <div style={{ color: "var(--color-text-primary)", whiteSpace: "nowrap" }}>
                            {new Date(closedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
                            {new Date(closedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: "var(--color-text-secondary)" }}>—</span>
                      )}
                    </td>
                    {!isWon && (
                      <td style={{ ...tdStyle, color: "var(--color-text-secondary)", maxWidth: 280 }}>
                        <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {lead.lostReason || "—"}
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && !isError && pageCount > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, paddingTop: 12 }}>
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{ background: "none", border: "1px solid var(--color-border-subtle)", borderRadius: 6, padding: "4px 8px", cursor: page === 0 ? "not-allowed" : "pointer", opacity: page === 0 ? 0.4 : 1, display: "inline-flex", alignItems: "center" }}
          >
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            Page {page + 1} of {pageCount}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={page >= pageCount - 1}
            style={{ background: "none", border: "1px solid var(--color-border-subtle)", borderRadius: 6, padding: "4px 8px", cursor: page >= pageCount - 1 ? "not-allowed" : "pointer", opacity: page >= pageCount - 1 ? 0.4 : 1, display: "inline-flex", alignItems: "center" }}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left", padding: "10px 14px",
  fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)",
};
const tdStyle = { padding: "12px 14px", verticalAlign: "top" };

export default function Pipeline({ idleFilterProp, onIdleFilterChange }) {
  const { data, isLoading, isError, error, refetch } = usePipelines();
  const { byId } = useTeamMembers();
  const { currency: orgCurrency } = useOrgCurrency();
  const pipelines = useMemo(() => data?.pipelines || [], [data]);
  const [selectedPipelineId, setSelectedPipelineId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [openLeadId, setOpenLeadId] = useState(null);
  // Board is the only view now — analytics tab was removed (it lived behind a
  // toggle that no one used). Keep the variable name out entirely; the inline
  // board renderer below assumes board.
  // Pipeline status filter — mirrors old CRM's Potential/Won/Lost tabs. When
  // active = open leads, board view. Won/Lost replaces the board with a flat
  // table view of closed leads in this project.
  const [statusFilter, setStatusFilter] = useState("active"); // 'active' | 'won' | 'lost'
  const [idleFilterInternal, setIdleFilterInternal] = useState("all");
  // Controlled from outside (Dashboard navigation) or self-managed internally.
  const idleFilter = idleFilterProp !== undefined ? idleFilterProp : idleFilterInternal;
  const setIdleFilter = onIdleFilterChange || setIdleFilterInternal;
  // Cache of leads loaded by WonLostList so the drawer can hydrate when a row
  // is clicked (these leads aren't in activePipeline.stages by definition).
  const [closedLeadsById, setClosedLeadsById] = useState({});
  const moveLead = useMoveLead();

  // Auto-select first pipeline whenever the list arrives or the selected one
  // disappears (e.g. deleted).
  const activePipeline = useMemo(() => {
    if (!pipelines.length) return null;
    if (selectedPipelineId) {
      const found = pipelines.find((p) => String(p._id) === String(selectedPipelineId));
      if (found) return found;
    }
    return pipelines[0];
  }, [pipelines, selectedPipelineId]);

  useEffect(() => {
    if (activePipeline && activePipeline._id !== selectedPipelineId) {
      setSelectedPipelineId(activePipeline._id);
    }
  }, [activePipeline, selectedPipelineId]);

  const openLead = useMemo(() => {
    if (!openLeadId) return null;
    // Won/Lost view — look in the closed-leads cache populated by WonLostList.
    if (statusFilter !== "active") {
      return closedLeadsById[String(openLeadId)] || null;
    }
    // Active board — walk the pipeline stages.
    if (!activePipeline) return null;
    for (const s of activePipeline.stages || []) {
      for (const e of s.leads || []) {
        if (String(e.leadId) === String(openLeadId)) return e.lead;
      }
    }
    return null;
  }, [openLeadId, activePipeline, statusFilter, closedLeadsById]);

  const handleMoveTo = ({ leadId, fromStageId }, toStageId) => {
    if (!activePipeline || fromStageId === toStageId) return;
    moveLead.mutate(
      { pipelineId: activePipeline._id, leadId, stageId: toStageId },
      { onError: (e) => toast.error(e?.response?.data?.error || "Move failed") },
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHeader
        title={activePipeline?.name || "Pipelines"}
        subtitle="Drag deals between stages to move them forward"
        actions={
          statusFilter === "active" && activePipeline && (activePipeline.stages || []).length > 0 ? (
            <Button variant="primary" size="sm" leftIcon={<UserPlus size={14} />} onClick={() => setBulkAddOpen(true)}>
              Add leads
            </Button>
          ) : null
        }
        topRight={<>
          {/* Open / Won / Lost status filter — matches the Leads page pill
              styling so the CRM has one consistent filter chrome. No icons,
              no per-tab tinting; active tab is just the surface-color pill. */}
          {activePipeline && (() => {
            const PIPELINE_STATUS_OPTIONS = [
              { key: "active", label: "Open" },
              { key: "won",    label: "Won"  },
              { key: "lost",   label: "Lost" },
            ];
            const selectedLabel = PIPELINE_STATUS_OPTIONS.find((o) => o.key === statusFilter)?.label || "Open";
            return (
              <Dropdown
                menu={{
                  items: PIPELINE_STATUS_OPTIONS,
                  selectedKeys: [statusFilter],
                  onClick: ({ key }) => setStatusFilter(key),
                }}
                trigger={["click"]}
              >
                <button type="button" className="tg-tool">
                  <span className="tg-tool-value">{selectedLabel}</span>
                  <ChevronDown size={13} />
                </button>
              </Dropdown>
            );
          })()}
          {statusFilter === "active" && activePipeline && (
            <div role="tablist" aria-label="Idle filter" style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
              {IDLE_FILTERS.map(({ key, label }) => {
                const active = idleFilter === key;
                return (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setIdleFilter(key)}
                    className="tg-tool"
                    style={active ? { background: "var(--color-hover-bg)", color: "var(--color-text-primary)", fontWeight: 500 } : undefined}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          {pipelines.length > 1 && statusFilter === "active" && (
            <AntSelect
              value={selectedPipelineId || undefined}
              onChange={(v) => setSelectedPipelineId(v)}
              style={{ minWidth: 180, height: 28 }}
              options={pipelines.map((p) => ({ value: p._id, label: p.name }))}
            />
          )}
        </>}
      />

      <div style={{ flex: 1, padding: "16px 24px 20px", overflow: "hidden" }}>
        {isLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 40, justifyContent: "center", color: "var(--color-text-secondary)" }}>
            <Loader2 size={18} className="animate-spin" /> Loading pipelines…
          </div>
        )}
        {isError && (
          <div style={{ background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 16, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 12 }}>
            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Couldn't load pipelines</div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>{error?.response?.data?.error || error?.message}</div>
              <button onClick={() => refetch()} style={{ marginTop: 10, background: "transparent", border: "1px solid currentColor", color: "currentColor", borderRadius: 8, padding: "4px 10px", fontSize: 13, cursor: "pointer" }}>Retry</button>
            </div>
          </div>
        )}
        {!isLoading && !isError && pipelines.length === 0 && (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ maxWidth: 360, textAlign: "center", color: "var(--color-text-secondary)" }}>
              <div style={{ width: 56, height: 56, borderRadius: 8, margin: "0 auto 16px", background: "var(--crm-primary-soft)", color: "var(--crm-primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <GitBranch size={24} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 6 }}>No pipelines yet</div>
              <div style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
                A pipeline is a kanban board where you drag leads through stages from qualification to close.
              </div>
              <Button variant="primary" leftIcon={<Plus size={14} />} onClick={() => setCreating(true)}>
                Create your first pipeline
              </Button>
            </div>
          </div>
        )}
        {!isLoading && !isError && activePipeline && statusFilter === "active" && (
          <div style={{ display: "flex", gap: 12, height: "100%", overflowX: "auto", overflowY: "hidden", paddingBottom: 8 }}>
            {(activePipeline.stages || []).map((stage) => (
              <StageColumn
                key={String(stage._id)}
                pipelineId={activePipeline._id}
                stage={stage}
                byId={byId}
                orgCurrency={orgCurrency}
                probability={stageProbability(stage, activePipeline.stages || [])}
                onCardClick={(lead) => setOpenLeadId(lead._id)}
                onMoveTo={handleMoveTo}
                idleFilter={idleFilter}
              />
            ))}
            <AddStageColumn pipelineId={activePipeline._id} />
          </div>
        )}
        {!isLoading && !isError && activePipeline && (statusFilter === "won" || statusFilter === "lost") && (
          <WonLostList
            status={statusFilter}
            byId={byId}
            orgCurrency={orgCurrency}
            onLeadClick={setOpenLeadId}
            onItemsLoaded={(items) => {
              setClosedLeadsById((prev) => {
                const next = { ...prev };
                for (const l of items) next[String(l._id)] = l;
                return next;
              });
            }}
          />
        )}
      </div>

      <NewPipelineModal open={creating} onClose={() => setCreating(false)} />
      <BulkAddLeadsModal open={bulkAddOpen} onClose={() => setBulkAddOpen(false)} pipeline={activePipeline} />
      <LeadDrawer open={!!openLead} onClose={() => setOpenLeadId(null)} lead={openLead} />
      <style>{`
        .crm-stage-lane::-webkit-scrollbar { width: 8px; }
        .crm-stage-lane::-webkit-scrollbar-track { background: transparent; }
        .crm-stage-lane::-webkit-scrollbar-thumb {
          background: var(--color-border-subtle);
          border-radius: 4px;
        }
        .crm-stage-lane:hover::-webkit-scrollbar-thumb {
          background: var(--color-text-secondary);
          opacity: 0.4;
        }
        .crm-stage-lane { scrollbar-width: thin; scrollbar-color: var(--color-border-subtle) transparent; }
      `}</style>
    </div>
  );
}
